<template>
    <figure :class="chartContainerClass">
        <div :class="chartContainerClassContent">
            <div class="chart--content" ref="chart"/>
        </div>
        <figcaption>
            <chart-legend :school-legend="legendItems" :type="type"/>
        </figcaption>
    </figure>
</template>

<script>
    import * as d3 from 'd3';
    import chartMixin from '../chart-mixin.js';
    import { MONOCHROME_DARK_GRAY, MONOCHROME_DARK_SILVER, MONOCHROME_WHITE } from '../constants';
    import ChartLegend from '../chart-legend/chart-legend.vue';

    export default {
        components: {
            ChartLegend,
        },
        mixins: [chartMixin],
        props: {
            type: {
                type: String,
                default: '',
            },
            data: {
                type: [String, Array],
                default: () => [],
            },
        },
        data() {
            return {
                legendItems: [],
            };
        },
        methods: {
            /* eslint-disable max-lines-per-function */
            renderHorizontalStackedBarChart() {
                const width = 720;
                const height = 320;
                const padding = 40;
                const barHeight = 42;

                const container = d3.select(this.$refs.legend);
                const svg = this.setupSVG(width, height);

                const uniqueNames = Array.from(new Set(this.chartData.map(d => d.name)));
                const uniqueTypes = Array.from(new Set(this.chartData.map(d => d.type)));

                const transformedData = uniqueNames.map(name => {
                    const segments = uniqueTypes.map(type => this.findSegment(name, type) || { name, type, value: 0, color: '#000' });
                    return { name, segments };
                });

                const xScale = d3.scaleLinear()
                    .domain([0, 1300])
                    .range([padding, width - padding]);

                const yScale = d3.scaleBand()
                    .domain(uniqueNames)
                    .range([padding, height - padding])
                    .padding(0.2);

                const xAxis = d3.axisTop(xScale)
                    .tickValues([260, 520, 780, 1040, 1300])
                    .tickFormat(d3.format('d'));

                const xAxisGroup = svg.append('g')
                    .attr('transform', `translate(0, ${ padding })`)
                    .call(xAxis);

                xAxisGroup.selectAll('.tick text')
                    .attr('y', padding - 55)
                    .attr('font-size', '12px')
                    .attr('fill', MONOCHROME_DARK_GRAY);

                xAxisGroup.selectAll('.tick line')
                    .attr('stroke', MONOCHROME_DARK_SILVER);

                xAxisGroup.select('.domain')
                    .attr('stroke', MONOCHROME_DARK_SILVER);

                svg.append('g')
                    .attr('transform', `translate(0, ${ padding })`);

                svg.append('text')
                    .attr('x', padding)
                    .attr('y', padding - 15)
                    .text('Teilnehmende')
                    .attr('font-size', '12px')
                    .attr('fill', MONOCHROME_DARK_GRAY);

                transformedData.forEach(d => {
                    let xOffset = padding;
                    let yPos = yScale(d.name);
                    d.segments.forEach(segment => {
                        const barWidth = xScale(segment.value) - padding;
                        svg.append('rect')
                            .attr('x', xOffset)
                            .attr('y', yPos)
                            .attr('width', barWidth)
                            .attr('height', barHeight)
                            .attr('fill', segment.color);

                        let textX;
                        let textColor;
                        if (barWidth > 50) {
                            textX = xOffset + 10;
                            textColor = MONOCHROME_WHITE;
                        } else {
                            textX = xOffset + barWidth + 10;
                            textColor = MONOCHROME_DARK_GRAY;
                        }

                        svg.append('text')
                            .attr('x', textX)
                            .attr('y', yPos + barHeight / 2)
                            .attr('dy', '0.35em')
                            .attr('fill', textColor)
                            .attr('font-size', '12px')
                            .text(segment.percentage);

                        xOffset += barWidth;
                    });
                });


                svg.selectAll()
                    .data(uniqueNames)
                    .enter()
                    .append('text')
                    .attr('x', +5)
                    .attr('y', d => yScale(d) + yScale.bandwidth() / 2.25)
                    .attr('dy', '0.35em')
                    .text(d => d)
                    .attr('font-size', '12px')
                    .attr('fill', MONOCHROME_DARK_GRAY)
                    .attr('text-achor', 'end');

                transformedData.forEach((d, i) => {
                    if (i < transformedData.length - 1) {
                        const yPosCurrentBar = yScale(d.name) + barHeight;
                        const yPosNextBar = yScale(transformedData[i + 1].name);
                        const yPosLine = (yPosCurrentBar + yPosNextBar) / 2;

                        svg.append('line')
                            .attr('x1', padding)
                            .attr('x2', width - padding)
                            .attr('y1', yPosLine)
                            .attr('y2', yPosLine)
                            .attr('stroke', '#ccc')
                            .attr('stroke-width', '1');
                    }
                });

                const colorMap = new Map();
                this.chartData.forEach(d => colorMap.set(d.color, d.type));

                const legendContainer = container.append('div')
                    .attr('class', 'chart--legend');

                const legendItemsContainer = legendContainer.append('div')
                    .attr('class', 'chart--legend-items');

                colorMap.forEach((legendText, legendColor) => {
                    const legendItem = legendItemsContainer.append('div')
                        .attr('class', 'chart--legend-item');

                    const dot = legendItem.append('svg')
                        .attr('width', 20)
                        .attr('height', 20);

                    dot.append('circle')
                        .attr('cx', 10)
                        .attr('cy', 10)
                        .attr('r', 5)
                        .style('fill', legendColor);

                    legendItem.append('p')
                        .attr('class', 'chart--legend-text')
                        .text(legendText);
                });
            },
            generateLegendItems() {
                const colorMap = new Map();
                this.chartData.forEach(d => colorMap.set(d.color, d.type));

                this.legendItems = Array.from(colorMap).map(([color, type]) => ({
                    legendColor: color,
                    legendText: type,
                }));
            },
        },
        mounted() {
            this.renderHorizontalStackedBarChart();
            this.generateLegendItems();
        },
    };
</script>
